import React, { useState, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { get, post } from "../../services/api";
import Select from "react-select";
import { toast } from "react-toastify";

function AttendanceSetup() {
  const token = localStorage.getItem("token");
  const organizationId = localStorage.getItem("organizationId");
  const savedCenterId =
    localStorage.getItem("centerId") === "null"
      ? null
      : localStorage.getItem("centerId");

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    trigger,
    formState: { errors, isSubmitting },
  } = useForm({});
  const sessionId = watch("sessionId");

  const [centerId, setCenterId] = useState(savedCenterId || null);
  const [centersData, setCentersData] = useState([]);
  const [attendanceData, setAttendanceData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sessionData, setSessionData] = useState([]);

  // Fetch sessions function
  const fetchSession = async () => {
    try {
      const response = await get(
        `Session/GetSessions?organizationId=${organizationId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.sessions || []; // Return the data instead of setting state
    } catch (error) {
      console.error("Error fetching sessions:", error);
      return [];
    }
  };

  // Initialize data - runs only once on mount
  useEffect(() => {
    const initializeData = async () => {
      try {
        // Fetch centers and sessions in parallel
        const [centersResponse, sessionsResponse] = await Promise.all([
          get(
            `Dropdown/GetAllCentersByOrganizationId?organizationId=${organizationId}`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          ),
          fetchSession(),
        ]);

        setCentersData(centersResponse);
        setSessionData(sessionsResponse);

        // Set center value if savedCenterId exists
        if (savedCenterId) {
          const selectedCenter = centersResponse.find(
            (center) => center.id === parseInt(savedCenterId, 10)
          );
          if (selectedCenter) {
            setValue("centerId", {
              value: selectedCenter.id,
              label: selectedCenter.name,
            });
          }
        }

        // Set session value if sessions exist
        if (sessionsResponse && sessionsResponse.length > 0) {
          setValue("sessionId", {
            value: sessionsResponse[0].id,
            label: sessionsResponse[0].name,
          });
        }
      } catch (error) {
        console.error("Error initializing data:", error);
      }
    };

    initializeData();
  }, [organizationId, token, savedCenterId, setValue]); // Proper dependencies

  // Fetch attendance data when centerId or sessionId changes
  useEffect(() => {
    const fetchCenterAndClassAttendance = async (id) => {
      if (!sessionId?.value) return; // Don't fetch if no session selected

      setLoading(true);
      try {
        const formData = {
          id: id,
          createdById: sessionId.value,
        };

        const response = await post(
          `CenterAttendence/GetCenterAttendenceAndClasses`,
          formData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        setAttendanceData(response.centerAndClasses || []);
      } catch (error) {
        console.error("Error fetching fetchCenterAndClassAttendance:", error);
        setAttendanceData([]);
      } finally {
        setLoading(false);
      }
    };

    if (centerId > 0 && sessionId?.value) {
      fetchCenterAndClassAttendance(centerId);
    } else {
      setAttendanceData([]); // Clear data if no center or session
    }
  }, [centerId, sessionId, token]); // Remove reset from dependencies

  const [selectAllClassWise, setSelectAllClassWise] = useState(false);
  const [selectAllSubjectWise, setSelectAllSubjectWise] = useState(false);

  const handleSelectAll = (type) => {
    setAttendanceData((prevData) => {
      return prevData.map((center) => ({
        ...center,
        classes: center.classes.map((classItem) => ({
          ...classItem,
          attendanceType: type,
        })),
      }));
    });

    if (type === 1) {
      setSelectAllClassWise(true);
      setSelectAllSubjectWise(false);
    } else if (type === 2) {
      setSelectAllClassWise(false);
      setSelectAllSubjectWise(true);
    } else {
      setSelectAllClassWise(false);
      setSelectAllSubjectWise(false);
    }
  };

  const handleCheckboxChange = (centerIndex, classIndex, type) => {
    setAttendanceData((prevData) => {
      const updatedData = [...prevData];
      updatedData[centerIndex].classes[classIndex].attendanceType = type;

      const allClassWiseChecked = updatedData.every((center) =>
        center.classes.every((classItem) => classItem.attendanceType === 1)
      );
      const allSubjectWiseChecked = updatedData.every((center) =>
        center.classes.every((classItem) => classItem.attendanceType === 2)
      );

      setSelectAllClassWise(allClassWiseChecked);
      setSelectAllSubjectWise(allSubjectWiseChecked);

      return updatedData;
    });
  };

  const saveData = async (data) => {
    const formData = attendanceData.map((center) => ({
      centerId: center.centerId,
      sessionId: sessionId.value,
      centerName: center.centerName,
      classes: center.classes.map((classItem) => ({
        id: classItem.id,
        classId: classItem.classId,
        className: classItem.className,
        attendanceType: classItem.attendanceType,
        createdBy: localStorage.getItem("userId"),
      })),
    }));

    try {
      const response = await post(
        "CenterAttendence/SaveCenterAttendenceAndClasses",
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (response.success) {
        toast.success("Success!");
      }
    } catch (error) {
      console.error("Error.", error);
      toast.error("Error saving data");
    }
  };

  return (
    <>
      <div className="page-header d-flex mb-2">
        <h2 className="page-heading fs-7">Attendance Setup</h2>
      </div>

      <div className="row g-3">
        <div className="col-md-12">
          <div className="card">
            <div className="card-body">
              <form className="" onSubmit={handleSubmit(saveData)}>
                <div className="page-header d-flex mb-2 align-items-center">
                  <div className="mb-3 me-3" style={{ width: "200px" }}>
                    <label className="form-label">Center</label>
                    <Controller
                      name="centerId"
                      control={control}
                      rules={{ required: "This field is required." }}
                      render={({ field }) => (
                        <Select
                          {...field}
                          options={centersData.map((center) => ({
                            value: center.id,
                            label: center.name,
                          }))}
                          isDisabled={savedCenterId !== "0"}
                          onChange={(selectedOption) => {
                            setValue("centerId", selectedOption);
                            setCenterId(selectedOption?.value || null);
                            trigger("centerId");
                          }}
                          className="react-select-style"
                          classNamePrefix="react-select-style-pref"
                        />
                      )}
                    />
                    {errors.centerId && (
                      <small className="text-danger">
                        {errors.centerId.message}
                      </small>
                    )}
                  </div>
                  <div className="mb-3 me-3" style={{ width: "200px" }}>
                    <label className="form-label">Session</label>
                    <Controller
                      name="sessionId"
                      control={control}
                      rules={{ required: "This field is required." }}
                      render={({ field }) => (
                        <Select
                          {...field}
                          options={sessionData.map((session) => ({
                            value: session.id,
                            label: session.name,
                          }))}
                          onChange={(selectedOption) => {
                            setValue("sessionId", selectedOption);
                            trigger("sessionId");
                          }}
                          className="react-select-style"
                          classNamePrefix="react-select-style-pref"
                        />
                      )}
                    />
                    {errors.sessionId && (
                      <small className="text-danger">
                        {errors.sessionId.message}
                      </small>
                    )}
                  </div>
                </div>

                <div className="row g-3">
                  <div className="col-md-6">
                    {loading ? (
                      <p>Loading...</p>
                    ) : (
                      <div className="table-responsive">
                      <table className="table table-bordered custom-table m-0">
                        <thead>
                          <tr>
                            <th>Center</th>
                            <th>Class</th>
                            <th className="text-center">
                              <div className="d-flex justify-content-center align-items-center">
                                <div className="form-check mb-1">
                                  <input
                                    type="checkbox"
                                    className="form-check-input"
                                    checked={selectAllClassWise}
                                    onChange={(e) =>
                                      handleSelectAll(e.target.checked ? 1 : 0)
                                    }
                                  />
                                </div>
                                Class Wise
                              </div>
                            </th>
                            <th className="text-center">
                              <div className="d-flex justify-content-center align-items-center">
                                <div className="form-check mb-1">
                                  <input
                                    type="checkbox"
                                    className="form-check-input"
                                    checked={selectAllSubjectWise}
                                    onChange={(e) =>
                                      handleSelectAll(e.target.checked ? 2 : 0)
                                    }
                                  />
                                </div>
                                Subject Wise
                              </div>
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {!attendanceData.length ? (
                            <tr className="text-center w-100">
                              <td colSpan="4">
                                There are no records to display
                              </td>
                            </tr>
                          ) : (
                            attendanceData.map((centerItem, centerIndex) =>
                              centerItem.classes.map((item, index) => (
                                <tr key={`${centerIndex}-${index}`}>
                                  {index === 0 && (
                                    <th rowSpan={centerItem.classes.length}>
                                      {centerItem.centerName}
                                    </th>
                                  )}
                                  <td>{item.className}</td>
                                  <td>
                                    <div className="d-flex justify-content-center">
                                      <div className="form-check">
                                        <input
                                          className="form-check-input"
                                          type="checkbox"
                                          checked={item.attendanceType === 1}
                                          onChange={() =>
                                            handleCheckboxChange(
                                              centerIndex,
                                              index,
                                              1
                                            )
                                          }
                                        />
                                      </div>
                                    </div>
                                  </td>
                                  <td>
                                    <div className="d-flex justify-content-center">
                                      <div className="form-check">
                                        <input
                                          className="form-check-input"
                                          type="checkbox"
                                          checked={item.attendanceType === 2}
                                          onChange={() =>
                                            handleCheckboxChange(
                                              centerIndex,
                                              index,
                                              2
                                            )
                                          }
                                        />
                                      </div>
                                    </div>
                                  </td>
                                </tr>
                              ))
                            )
                          )}
                        </tbody>
                      </table>
                      </div>
                    )}
                  </div>

                  <div className="col-12">
                    <button
                      className="btn btn-primary btn-sm"
                      type="submit"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? "Saving..." : "Save"}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default AttendanceSetup;
